import { useState, useEffect, useMemo, useCallback } from 'react';
import { outletApi } from 'src/api/outletApi';
import { useAllOutlets, useOutlets } from 'src/hooks/useOutlets';

const useOutletManagement = (initialLoadCount = 300) => {
  const [outlets, setOutlets] = useState([]);
  const [allOutletNames, setAllOutletNames] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFavorites, setExpandedFavorites] = useState(true);
  const [expandedBlacklisted, setExpandedBlacklisted] = useState(true);

  // Use SWR hooks instead of direct API calls
  const { outlets: managedOutlets, loading: managedOutletsLoading, error: managedOutletsError, mutate: mutateOutlets } = useOutlets();
  const {
    outlets: allOutlets,
    allOutlets: fullOutletsList, // Full dataset for searching
    loading: allOutletsLoading,
    error: allOutletsError,
    mutate: mutateAllOutlets,
    loadMore,
    hasMore,
    totalCount,
  } = useAllOutlets(initialLoadCount);

  // Combine loading states
  const loading = managedOutletsLoading || allOutletsLoading;

  // Update state when SWR data is loaded
  useEffect(() => {
    if (managedOutlets && Array.isArray(managedOutlets)) {
      // Transform API data to match our component's expected format
      const transformedOutlets = managedOutlets.map((item) => ({
        id: item.id,
        name: item.outlet,
        category: item.type,
        type: item.type === 'blacklisted' ? 'blacklisted' : item.type === 'favourited' ? 'favourited' : null,
      }));

      setOutlets(transformedOutlets);
    } else {
      // If managedOutlets is not an array, set outlets to empty array
      setOutlets([]);
    }
  }, [managedOutlets]);

  useEffect(() => {
    if (fullOutletsList && Array.isArray(fullOutletsList)) {
      // Filter out empty strings and duplicates
      const filteredOutletNames = fullOutletsList
        .filter((name) => name && typeof name === 'string' && name.trim() !== '')
        .filter((name, index, self) => self.indexOf(name) === index);

      setAllOutletNames(filteredOutletNames);
    } else {
      // If fullOutletsList is not an array, set allOutletNames to empty array
      setAllOutletNames([]);
    }
  }, [fullOutletsList]);

  // Set error state if either SWR hook returns an error
  useEffect(() => {
    if (managedOutletsError) {
      setError('Failed to load outlets. Please try again later.');
      console.error('Failed to fetch outlets:', managedOutletsError);
    } else if (allOutletsError) {
      console.error('Failed to fetch all outlet names:', allOutletsError);
      // Don't set error state here as we still have the main outlets data
    } else {
      setError(null);
    }
  }, [managedOutletsError, allOutletsError]);

  const handleTypeChange = async (outletId, newType, outletName = null) => {
    try {
      // If outletId is null, we're dealing with an unmanaged outlet
      if (outletId === null) {
        if (!outletName) {
          setError('Outlet name is required.');
          return;
        }

        // For unmanaged outlets, submit with the requested type to create a new managed outlet
        await outletApi.submitOutlet(outletName, newType || '');

        // Optimistically add the new outlet to the UI
        const newOutlet = {
          // Temporary ID until refresh
          id: `temp-${Date.now()}`,
          name: outletName,
          type: newType,
        };

        setOutlets((prevOutlets) => [...prevOutlets, newOutlet]);
      } else {
        // For managed outlets, update with the requested type
        // Find the outlet before updating
        const outletToUpdate = outlets.find((o) => o.id === outletId);
        if (!outletToUpdate) {
          setError('Outlet not found.');
          return;
        }

        // Check if we need to delete the outlet
        // This happens when newType is null and the outlet already has a type
        // (which means user clicked on an already active button)
        const shouldDelete = newType === null && (outletToUpdate.type === 'favourited' || outletToUpdate.type === 'blacklisted');

        // Update UI optimistically
        if (shouldDelete) {
          // If deleting, remove from the outlets list
          setOutlets((prevOutlets) => prevOutlets.filter((outlet) => outlet.id !== outletId));

          await outletApi.deleteOutlet(outletId);
        } else {
          // Otherwise update as before
          setOutlets((prevOutlets) => prevOutlets.map((outlet) => (outlet.id === outletId ? { ...outlet, type: newType } : outlet)));

          // Use updateOutlet API for managed outlets
          const updatedType = newType || ''; // Use empty string as default type when type is null

          await outletApi.updateOutlet(outletId, outletToUpdate.name, updatedType);
        }
      }

      // Refresh the outlets data using SWR's mutate function
      await mutateOutlets();
      await mutateAllOutlets();

      setError(null);
    } catch (err) {
      console.error('Failed to update outlet type:', err);
      setError('Failed to update outlet type. Please try again.');

      // Refresh the outlets data using SWR's mutate function
      try {
        await mutateOutlets();
      } catch (refreshErr) {
        console.error('Failed to refresh outlets after error:', refreshErr);
      }
    }
  };

  // Create a combined list of all outlets (managed + unmanaged)
  const allOutletsList = useMemo(() => {
    // Get all managed outlet names
    const managedOutletNames = new Set(outlets.map((outlet) => outlet.name));

    // Create objects for unmanaged outlets from the visible subset
    const unmanagedOutlets = allOutletNames
      .filter((name) => !managedOutletNames.has(name))
      .map((name) => ({
        name,
        type: null,
      }));

    // Return combined list
    return [...outlets, ...unmanagedOutlets];
  }, [outlets, allOutletNames]);

  // For searching, we need to search the entire dataset
  const filteredOutlets = useMemo(() => {
    if (!searchQuery) {
      // When not searching, only show the initially loaded outlets
      // This ensures we don't display all 3000 outlets at once
      return allOutletsList.slice(0, allOutlets.length);
    }

    // If we're searching, we need to search across all outlets, not just the loaded ones
    if (searchQuery && fullOutletsList && Array.isArray(fullOutletsList)) {
      // Get all managed outlet names for checking
      const managedOutletNames = new Set(outlets.map((outlet) => outlet.name));
      const managedOutletsMap = new Map(outlets.map((outlet) => [outlet.name, outlet]));

      // First, filter the full list of outlet names by the search query
      const matchingOutletNames = fullOutletsList.filter(
        (name) => name && typeof name === 'string' && name.toLowerCase().includes(searchQuery.toLowerCase())
      );

      // Then convert these names to outlet objects
      return matchingOutletNames.map((name) => {
        // If it's a managed outlet, use the existing outlet object
        if (managedOutletNames.has(name)) {
          return managedOutletsMap.get(name);
        }
        // Otherwise create a new unmanaged outlet object
        return {
          name,
          type: null,
        };
      });
    }

    // Fallback to filtering the visible subset
    return allOutletsList.filter(
      (outlet) =>
        outlet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (outlet.category && outlet.category.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [allOutletsList, searchQuery, fullOutletsList, outlets, allOutlets.length]);

  // Load more data when user scrolls to the bottom
  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      loadMore(initialLoadCount); // Load more items
    }
  }, [hasMore, loadMore, initialLoadCount]);

  const favouritedOutlets = useMemo(() => outlets.filter((outlet) => outlet.type === 'favourited'), [outlets]);

  const blacklistedOutlets = useMemo(() => outlets.filter((outlet) => outlet.type === 'blacklisted'), [outlets]);

  return {
    // State
    outlets,
    allOutletNames,
    error,
    searchQuery,
    expandedFavorites,
    expandedBlacklisted,
    loading,

    // Derived data
    allOutletsList,
    filteredOutlets,
    favouritedOutlets,
    blacklistedOutlets,
    allOutlets,
    totalCount,
    hasMore,

    // Actions
    setSearchQuery,
    setExpandedFavorites,
    setExpandedBlacklisted,
    handleTypeChange,
    handleLoadMore,
  };
};

export default useOutletManagement;
